import { useState, useEffect, useCallback } from 'react'

/**
 * Tailwind CSS default breakpoints
 * sm: 640px
 * md: 768px
 * lg: 1024px
 * xl: 1280px
 * 2xl: 1536px
 */
export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const

export type Breakpoint = keyof typeof breakpoints

/**
 * Hook to detect if a media query matches
 * @param query - CSS media query string
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches
    }
    return false
  })

  useEffect(() => {
    if (typeof window === 'undefined') return

    const mediaQuery = window.matchMedia(query)

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }

    // Set initial value on mount via handler pattern
    handler({ matches: mediaQuery.matches } as MediaQueryListEvent)

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [query])

  return matches
}

/**
 * Hook to detect current breakpoint
 * @returns Object with boolean flags for each breakpoint
 */
export function useBreakpoint() {
  const isSm = useMediaQuery(`(min-width: ${breakpoints.sm}px)`)
  const isMd = useMediaQuery(`(min-width: ${breakpoints.md}px)`)
  const isLg = useMediaQuery(`(min-width: ${breakpoints.lg}px)`)
  const isXl = useMediaQuery(`(min-width: ${breakpoints.xl}px)`)
  const is2xl = useMediaQuery(`(min-width: ${breakpoints['2xl']}px)`)

  return {
    isSm,
    isMd,
    isLg,
    isXl,
    is2xl,
    // Convenience helpers
    isMobile: !isMd, // Below 768px
    isTablet: isMd && !isLg, // 768px - 1023px
    isDesktop: isLg, // 1024px and above
  }
}

/**
 * Hook to detect if viewport is below a certain breakpoint
 * @param breakpoint - Breakpoint to check against
 * @returns boolean indicating if viewport is below the breakpoint
 */
export function useIsMobile(breakpoint: Breakpoint = 'md'): boolean {
  return useMediaQuery(`(max-width: ${breakpoints[breakpoint] - 1}px)`)
}

/**
 * Hook to get current viewport dimensions
 * @returns Object with width and height
 */
export function useViewportSize() {
  const [size, setSize] = useState<{ width: number; height: number }>(() => {
    if (typeof window !== 'undefined') {
      return { width: window.innerWidth, height: window.innerHeight }
    }
    return { width: 0, height: 0 }
  })

  const handleResize = useCallback(() => {
    setSize({ width: window.innerWidth, height: window.innerHeight })
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [handleResize])

  return size
}
